import { endOfDay, getYear, isWithinInterval, startOfDay } from 'date-fns';
import christmasSlide from './sliders/login-page-slide-christmas.png';
import easterSlide from './sliders/login-page-slide-easter.png';
import newYearSlide from './sliders/login-page-slide-new-year.png';
import easterLogo from './logos/omnidots-logo-easter.svg';
import halloweenLogo from './logos/omnidots-logo-halloween.svg';
import laborsDayLogo from './logos/omnidots-laborsday.svg';
import sinterklaasLogo from './logos/omnidots-logo-sinterklaas.png';
import christmasLogo from './logos/omnidots-logo-christmas.png';
import newYearEveLogo from './logos/omnidots-logo-new-year-eve.svg';
import newYearDayLogo from './logos/omnidots-logo-new-year-day.gif';

type CountryCode = string;

export enum CountryGroup {
    EUROPE = 'EUROPE',
    NORTH_AMERICA = 'NORTH_AMERICA',
    AUSTRALIA_NEW_ZEALAND = 'AUSTRALIA_NEW_ZEALAND',
    MUSLIM_MAJORITY = 'MUSLIM_MAJORITY',
}

interface RecurringDateRange {
    start: {
        month: number;
        day: number;
    };
    end: {
        month: number;
        day: number;
    };
}

export interface SeasonalThemeContext {
    date: Date;
    timezone?: string;
}

interface NormalizedContext {
    date: Date;
    countryCodes: CountryCode[];
    timezone?: string | null;
}

export interface SeasonalLoginSlideButton {
    text: string;
    link: string;
}

export interface SeasonalLoginSlideConfig {
    src: string;
    title: string;
    description: string;
    button?: SeasonalLoginSlideButton;
}

export interface SeasonalLogoPayload {
    altText: string;
    asset: string;
}

export interface SeasonalThemeEntry {
    matcher: (context: NormalizedContext) => boolean;
    countries?: CountryCode[];
    countryGroups?: CountryGroup[];
    logo?: SeasonalLogoPayload;
    loginSlide?: SeasonalLoginSlideConfig;
}

const COUNTRY_GROUPS: Record<CountryGroup, CountryCode[]> = {
    [CountryGroup.EUROPE]: [
        'AL',
        'AT',
        'BA',
        'BE',
        'BG',
        'BY',
        'CH',
        'CY',
        'CZ',
        'DE',
        'DK',
        'EE',
        'ES',
        'FI',
        'FR',
        'GB',
        'GR',
        'HR',
        'HU',
        'IE',
        'IS',
        'IT',
        'LI',
        'LT',
        'LU',
        'LV',
        'ME',
        'MK',
        'MT',
        'NL',
        'NO',
        'PL',
        'PT',
        'RO',
        'RS',
        'SE',
        'SI',
        'SK',
        'UA',
    ],
    [CountryGroup.NORTH_AMERICA]: ['US', 'CA', 'MX'],
    [CountryGroup.AUSTRALIA_NEW_ZEALAND]: ['AU', 'NZ'],
    [CountryGroup.MUSLIM_MAJORITY]: [
        'AE',
        'AF',
        'AL',
        'AZ',
        'BD',
        'BH',
        'BN',
        'DJ',
        'DZ',
        'EG',
        'ID',
        'IQ',
        'IR',
        'JO',
        'KW',
        'KZ',
        'LB',
        'LY',
        'MA',
        'ML',
        'MR',
        'MY',
        'OM',
        'PK',
        'PS',
        'QA',
        'SA',
        'SD',
        'SO',
        'SY',
        'TD',
        'TN',
        'TR',
        'YE',
    ],
};

const TIMEZONE_TO_COUNTRY: Record<string, CountryCode> = {
    'Europe/Amsterdam': 'NL',
    'Europe/Brussels': 'BE',
    'Europe/Paris': 'FR',
    'Europe/Berlin': 'DE',
    'Europe/London': 'GB',
    'Europe/Dublin': 'IE',
    'Europe/Oslo': 'NO',
    'Europe/Stockholm': 'SE',
    'Europe/Copenhagen': 'DK',
    'Europe/Helsinki': 'FI',
    'Europe/Rome': 'IT',
    'Europe/Madrid': 'ES',
    'Europe/Lisbon': 'PT',
    'America/New_York': 'US',
    'America/Chicago': 'US',
    'America/Denver': 'US',
    'America/Los_Angeles': 'US',
    'America/Toronto': 'CA',
    'America/Vancouver': 'CA',
    'Australia/Sydney': 'AU',
    'Australia/Melbourne': 'AU',
    'Australia/Brisbane': 'AU',
    'Australia/Perth': 'AU',
    'Pacific/Auckland': 'NZ',
    'Asia/Dubai': 'AE',
    'Asia/Riyadh': 'SA',
    'Asia/Jakarta': 'ID',
    'Asia/Kuala_Lumpur': 'MY',
    'Asia/Karachi': 'PK',
    'Asia/Dhaka': 'BD',
    'Asia/Tehran': 'IR',
    'Africa/Cairo': 'EG',
    'Africa/Casablanca': 'MA',
};

function mapTimezoneToCountry(timezone?: string): CountryCode | undefined {
    if (!timezone) {
        return undefined;
    }
    return TIMEZONE_TO_COUNTRY[timezone];
}

function resolveCountries(entry: SeasonalThemeEntry): CountryCode[] {
    const fromGroups = entry.countryGroups?.flatMap((group) => COUNTRY_GROUPS[group] ?? []) ?? [];
    const direct = entry.countries ?? [];
    return Array.from(new Set([...fromGroups, ...direct]));
}

export function getThemeCountries(entry: SeasonalThemeEntry): CountryCode[] {
    return resolveCountries(entry);
}

function audienceScore(entry: SeasonalThemeEntry, context: NormalizedContext) {
    const requiredCountries = resolveCountries(entry);
    if (requiredCountries.length === 0) {
        // Global themes get a small baseline score.
        return 0.01;
    }

    const matches = requiredCountries.filter((country) => context.countryCodes.includes(country));
    if (matches.length === 0) {
        return 0;
    }

    // More specific (shorter list) audiences score higher.
    return 1 / requiredCountries.length;
}

function isWithinRecurringRange(date: Date, range: RecurringDateRange) {
    const intervals = [-1, 0, 1].map((offset) => {
        const baseYear = getYear(date) + offset;
        const start = startOfDay(new Date(baseYear, range.start.month - 1, range.start.day));
        let endYear = baseYear;
        if (
            range.end.month < range.start.month ||
            (range.end.month === range.start.month && range.end.day < range.start.day)
        ) {
            endYear += 1;
        }
        const end = endOfDay(new Date(endYear, range.end.month - 1, range.end.day));
        return { start, end };
    });

    return intervals.some((interval) => isWithinInterval(date, interval));
}

function normalizeContext({ date, timezone }: SeasonalThemeContext): NormalizedContext {
    const countryCodes = new Set<CountryCode>();

    const timezoneCountry = mapTimezoneToCountry(timezone);
    if (timezoneCountry) {
        countryCodes.add(timezoneCountry);
    }

    return {
        date,
        countryCodes: Array.from(countryCodes),
        timezone,
    };
}

export const HOLIDAY_THEMES: SeasonalThemeEntry[] = [
    {
        matcher: (context) =>
            getYear(context.date) === 2026 &&
            isWithinRecurringRange(context.date, {
                start: { month: 5, day: 1 },
                end: { month: 5, day: 1 },
            }) &&
            !context.countryCodes.some((country) => ['US', 'CA'].includes(country)),
        logo: {
            altText: gettext('LABOR_DAY_LOGO_ALT_TEXT'),
            asset: laborsDayLogo,
        },
    },
    {
        matcher: (context) =>
            isWithinRecurringRange(context.date, {
                start: { month: 4, day: 1 },
                end: { month: 4, day: 6 },
            }),
        logo: {
            altText: gettext('EASTER_LOGO_ALT_TEXT'),
            asset: easterLogo,
        },
        loginSlide: {
            src: easterSlide,
            title: gettext('LOGIN_PAGE_SLIDE_EASTER_TITLE'),
            description: gettext('LOGIN_PAGE_SLIDE_EASTER_DESCRIPTION'),
        },
    },
    {
        matcher: (context) =>
            isWithinRecurringRange(context.date, {
                start: { month: 10, day: 1 },
                end: { month: 10, day: 31 },
            }),
        logo: {
            altText: gettext('HALLOWEEN_LOGO_ALT_TEXT'),
            asset: halloweenLogo,
        },
    },
    {
        matcher: (context) =>
            isWithinRecurringRange(context.date, {
                start: { month: 11, day: 18 },
                end: { month: 12, day: 5 },
            }),
        countries: ['NL'],
        logo: {
            altText: gettext('SINTERKLAAS_LOGO_ALT_TEXT'),
            asset: sinterklaasLogo,
        },
    },
    {
        matcher: (context) =>
            isWithinRecurringRange(context.date, {
                start: { month: 12, day: 1 },
                end: { month: 12, day: 26 },
            }),
        countryGroups: [
            CountryGroup.EUROPE,
            CountryGroup.NORTH_AMERICA,
            CountryGroup.AUSTRALIA_NEW_ZEALAND,
        ],
        logo: {
            altText: gettext('CHRISTMAS_LOGO_ALT_TEXT'),
            asset: christmasLogo,
        },
        loginSlide: {
            src: christmasSlide,
            title: gettext('LOGIN_PAGE_SLIDE_CHRISTMAS_TITLE'),
            description: gettext('LOGIN_PAGE_SLIDE_CHRISTMAS_DESCRIPTION'),
        },
    },
    {
        matcher: (context) =>
            isWithinRecurringRange(context.date, {
                start: { month: 12, day: 31 },
                end: { month: 12, day: 31 },
            }),
        logo: {
            altText: gettext('NEW_YEAR_EVE_LOGO_ALT_TEXT'),
            asset: newYearEveLogo,
        },
    },
    {
        matcher: (context) =>
            isWithinRecurringRange(context.date, {
                start: { month: 1, day: 1 },
                end: { month: 1, day: 6 },
            }),
        logo: {
            altText: gettext('NEW_YEAR_LOGO_ALT_TEXT'),
            asset: newYearDayLogo,
        },
        loginSlide: {
            src: newYearSlide,
            title: gettext('LOGIN_PAGE_SLIDE_NEW_YEAR_TITLE'),
            description: gettext('LOGIN_PAGE_SLIDE_NEW_YEAR_DESCRIPTION'),
        },
    },
];

export function getSeasonalThemeDecision(
    context: SeasonalThemeContext,
    predicate: (entry: SeasonalThemeEntry) => boolean = () => true
): SeasonalThemeEntry | null {
    const normalized = normalizeContext(context);

    const scored = HOLIDAY_THEMES.map((entry, idx) => ({ entry, idx }))
        .filter(({ entry }) => entry.matcher(normalized))
        .filter(({ entry }) => predicate(entry))
        .map(({ entry, idx }) => {
            const audience = audienceScore(entry, normalized);
            if (audience === 0) {
                return null;
            }
            return { entry, score: audience, idx };
        })
        .filter(Boolean) as { entry: SeasonalThemeEntry; score: number; idx: number }[];

    if (scored.length === 0) {
        return null;
    }

    const best = scored.sort((a, b) => {
        if (b.score !== a.score) {
            return b.score - a.score;
        }
        return a.idx - b.idx;
    })[0].entry;

    return best;
}
